import { defineComponent as _defineComponent } from 'vue'
import { User, IntakeType, ExpensesMode } from "@/CodegenTypes";
import { mutations, queries } from "@/apollo";
import { clone } from "lodash";
import { errorMutationNotification } from "@/utils";
import { ZERO_TO_HUNDRED_DECIMAL_PERCENT, ZERO_TO_HUNDRED_PERCENT } from "@/constants";
import { computed, onMounted, ref, watch } from "vue";
import { addSuccessToast } from "@/vue-service";
import { apolloClient } from "@/apollo";
import { useTranslation } from "@/store/translation";
import { FEATURE_TOGGLES } from "@/config";


export default /*#__PURE__*/_defineComponent({
  __name: 'DDGameSettingForm',
  props: {
    upline: null,
    user: null
  },
  emits: ["close"],
  setup(__props: any, { emit }) {

const props = __props as {
  upline: User;
  user: User;
};



const form = ref(getDefaultForm());
const loading = ref(false);

const { $t } = useTranslation();

const canIntake = computed(() => {
  const isMember = props.user.roleName === "member";
  const uplineEnabledIntake = props.upline.ddDownlineIntake;
  return !isMember && uplineEnabledIntake;
});

const showPlaceOut = computed(() => {
  return props.user.roleName !== "member" && props.upline.ddPlaceoutPl > 0;
});

const showCompanyPl = computed(() => {
  return props.user.roleName !== "member";
});

const showUplinePl = computed(() => {
  return props.user.roleName !== "member";
});

const showManagementExpense = computed(() => {
  return !FEATURE_TOGGLES.useV1Legacy && props.user.roleName === "manager"
});

onMounted(() => {
  resetForm();
});

const currUserId = computed(() => {
  return props.user.userId;
});

function toggleModal(val: boolean) {
  if (!val) {
    emit("close");
  }
}

function getDefaultForm() {
  return clone({
    ddEnabled: props.user.ddEnabled,
    ddManagementExpensesMode: props.user.ddManagementExpensesMode,
    ddManagementExpenses: props.user.ddManagementExpenses,
    ddManagementExpensesManagerTicketInPercent: props.user.ddManagementExpensesManagerTicketInPercent,

    ddTicketRebate: props.user.ddTicketRebate,
    ddCompanyPl: props.user.ddCompanyPl,
    ddUplineIntakePl: props.user.ddUplineIntakePl,
    ddPlaceoutPl: props.user.ddPlaceoutPl,
    ddIntake: props.user.ddIntake,
    ddIntakeBig: props.user.ddIntakeBig,
    ddIntakeSmall: props.user.ddIntakeSmall,
    ddIntakeMethod: props.user.ddIntakeMethod,
    ddIntakeTax: props.user.ddIntakeTax,
    ddDownlineIntake: props.user.ddDownlineIntake,
    ddShareCompanyExpenses: props.user.ddShareCompanyExpenses,
    ddFirstPrizeCommission: props.user.ddFirstPrizeCommission,
    ddSecondPrizeCommission: props.user.ddSecondPrizeCommission,
    ddThirdPrizeCommission: props.user.ddThirdPrizeCommission,
    ddStarterPrizeCommission: props.user.ddStarterPrizeCommission,
    ddConsolationPrizeCommission: props.user.ddConsolationPrizeCommission,
  });
}

watch(
  () => props.user,
  () => {
    resetForm();
  }
);

function resetForm() {
  form.value = getDefaultForm();
}

const ticketRebateOptions = computed(() => {
  return [
    { label: "0.0%", value: 0 },
    { label: "0.5%", value: 0.5 },
    { label: "1.0%", value: 1 },
    { label: "1.5%", value: 1.5 },
    { label: "2.0%", value: 2 },
    { label: "2.5%", value: 2.5 },
    { label: "3.0%", value: 3 },
    { label: "3.5%", value: 3.5 },
    { label: "4.0%", value: 4 },
    { label: "4.5%", value: 4.5 },
    { label: "5.0%", value: 5 },
    { label: "5.5%", value: 5.5 },
    { label: "6.0%", value: 6 },
    { label: "6.5%", value: 6.5 },
    { label: "7.0%", value: 7 },
    { label: "7.5%", value: 7.5 },
    { label: "8.0%", value: 8 },
    { label: "8.5%", value: 8.5 },
    { label: "9.0%", value: 9 },
    { label: "9.5%", value: 9.5 },
    { label: "10.0%", value: 10 },
    { label: "10.5%", value: 10.5 },
    { label: "11.0%", value: 11 },
    { label: "11.5%", value: 11.5 },
    { label: "12.0%", value: 12 },
    { label: "12.5%", value: 12.5 },
    { label: "13.0%", value: 13 },
    { label: "13.5%", value: 13.5 },
    { label: "14.0%", value: 14 },
    { label: "14.5%", value: 14.5 },
    { label: "15.0%", value: 15 },
  ];
});

const expensesPercentOptions = computed(() => {
  const options = ZERO_TO_HUNDRED_DECIMAL_PERCENT;
  return options;
});
const companyPlOptions = computed(() => {
  const options = ZERO_TO_HUNDRED_PERCENT;
  if (props.user.intakeType !== "shadow") {
    return options.filter((opt) => opt.value <= props.upline.ddCompanyPl);
  }
  return options;
});

const placeoutPlOptions = computed(() => {
  if (FEATURE_TOGGLES.useV1Legacy) {
    return ZERO_TO_HUNDRED_PERCENT.filter(
      (opt) => opt.value <= props.upline.ddPlaceoutPl
    );
  }
  return ZERO_TO_HUNDRED_PERCENT;
});

const uplineIntakePlOptions = computed(() => {
  const options = ZERO_TO_HUNDRED_PERCENT;
  if (props.user.intakeType !== IntakeType.Stack) {
    return options.filter((opt) => opt.value <= props.upline.ddUplineIntakePl);
  } else {
    return options;
  }
});

// const intakeTaxOptions = computed(() => {
//   return [
//     { label: "0.00%", value: 0.0 },
//     { label: "0.05%", value: 0.05 },
//     { label: "0.10%", value: 0.1 },
//     { label: "0.15%", value: 0.15 },
//     { label: "0.20%", value: 0.2 },
//     { label: "0.25%", value: 0.25 },
//     { label: "0.30%", value: 0.3 },
//     { label: "0.35%", value: 0.35 },
//     { label: "0.40%", value: 0.4 },
//     { label: "0.45%", value: 0.45 },
//     { label: "0.50%", value: 0.5 },
//     { label: "0.55%", value: 0.55 },
//     { label: "0.60%", value: 0.6 },
//     { label: "0.65%", value: 0.65 },
//     { label: "0.70%", value: 0.7 },
//     { label: "0.75%", value: 0.75 },
//     { label: "0.80%", value: 0.8 },
//     { label: "0.85%", value: 0.85 },
//     { label: "0.90%", value: 0.9 },
//     { label: "0.95%", value: 0.95 },
//     { label: "1.00%", value: 1.0 },
//     { label: "1.05%", value: 1.05 },
//     { label: "1.10%", value: 1.1 },
//     { label: "1.15%", value: 1.15 },
//     { label: "1.20%", value: 1.2 },
//     { label: "1.25%", value: 1.25 },
//     { label: "1.30%", value: 1.3 },
//     { label: "1.35%", value: 1.35 },
//     { label: "1.40%", value: 1.4 },
//     { label: "1.45%", value: 1.45 },
//     { label: "1.50%", value: 1.5 },
//     { label: "1.55%", value: 1.55 },
//     { label: "1.60%", value: 1.6 },
//     { label: "1.65%", value: 1.65 },
//     { label: "1.70%", value: 1.7 },
//     { label: "1.75%", value: 1.75 },
//     { label: "1.80%", value: 1.8 },
//     { label: "1.85%", value: 1.85 },
//     { label: "1.90%", value: 1.9 },
//     { label: "1.95%", value: 1.95 },
//     { label: "2.00%", value: 2.0 },
//     { label: "2.00%", value: 2.0 },
//     { label: "2.05%", value: 2.05 },
//     { label: "2.10%", value: 2.1 },
//     { label: "2.15%", value: 2.15 },
//     { label: "2.20%", value: 2.2 },
//     { label: "2.25%", value: 2.25 },
//     { label: "2.30%", value: 2.3 },
//     { label: "2.35%", value: 2.35 },
//     { label: "2.40%", value: 2.4 },
//     { label: "2.45%", value: 2.45 },
//     { label: "2.50%", value: 2.5 },
//     { label: "2.55%", value: 2.55 },
//     { label: "2.60%", value: 2.6 },
//     { label: "2.65%", value: 2.65 },
//     { label: "2.70%", value: 2.7 },
//     { label: "2.75%", value: 2.75 },
//     { label: "2.80%", value: 2.8 },
//     { label: "2.85%", value: 2.85 },
//     { label: "2.90%", value: 2.9 },
//     { label: "2.95%", value: 2.95 },
//     { label: "3.00%", value: 3.0 },
//   ];
// });

const intakeMethodOptions = computed(() => {
  const isManager = props.user.roleName === "manager";
  const result = [
    { label: "Big Small", value: "bigSmall" },
    { label: "Priority Big", value: "priorityBig" },
    { label: "Priority Small", value: "prioritySmall" },
    { label: "Eat Big Turn Small", value: "eatBigTurnSmall" },
  ];
  if (isManager) {
    return result;
  } else {
    return result.filter(({ value }) => value !== "eatBigTurnSmall");
  }
});

const prizeCommissionOptions = computed(() => {
  return [
    { label: "0.0%", value: 0 },
    { label: "0.5%", value: 0.5 },
    { label: "1.0%", value: 1 },
    { label: "1.5%", value: 1.5 },
    { label: "2.0%", value: 2 },
    { label: "2.5%", value: 2.5 },
    { label: "3.0%", value: 3 },
    { label: "3.5%", value: 3.5 },
    { label: "4.0%", value: 4 },
    { label: "4.5%", value: 4.5 },
    { label: "5.0%", value: 5 },
    { label: "5.5%", value: 5.5 },
    { label: "6.0%", value: 6 },
    { label: "6.5%", value: 6.5 },
    { label: "7.0%", value: 7 },
    { label: "7.5%", value: 7.5 },
    { label: "8.0%", value: 8 },
    { label: "8.5%", value: 8.5 },
    { label: "9.0%", value: 9 },
    { label: "9.5%", value: 9.5 },
    { label: "10.0%", value: 10 },
    { label: "10.5%", value: 10.5 },
    { label: "11.0%", value: 11 },
    { label: "11.5%", value: 11.5 },
    { label: "12.0%", value: 12 },
    { label: "12.5%", value: 12.5 },
    { label: "13.0%", value: 13 },
    { label: "13.5%", value: 13.5 },
    { label: "14.0%", value: 14 },
    { label: "14.5%", value: 14.5 },
    { label: "15.0%", value: 15 },
  ];
});

async function handleSubmit() {
  loading.value = true;
  try {
    const legacyExpensePayload = FEATURE_TOGGLES.useV1Legacy
      ? {
          ddShareCompanyExpenses: form.value.ddShareCompanyExpenses,
        }
      : {
          ddManagementExpensesMode: form.value.ddManagementExpensesMode,
          ddManagementExpenses:
            form.value.ddManagementExpensesMode === ExpensesMode.Fixed
              ? form.value.ddManagementExpenses
              : undefined,
          ddManagementExpensesManagerTicketInPercent:
            form.value.ddManagementExpensesMode ===
            ExpensesMode.ManagerTicketInPercent
              ? form.value.ddManagementExpensesManagerTicketInPercent
              : undefined,
        };

    const result = await apolloClient.mutate({
      mutation: mutations.updateGameSetting,
      variables: {
        payload: {
          behalfOf: currUserId.value,
          ddEnabled: form.value.ddEnabled,
          ...legacyExpensePayload,
          ddTicketRebate: form.value.ddTicketRebate,
          ddCompanyPl: form.value.ddCompanyPl,
          ddUplineIntakePl: form.value.ddUplineIntakePl,
          ddPlaceoutPl: form.value.ddPlaceoutPl,
          ddIntake: form.value.ddIntake,
          ddIntakeBig: form.value.ddIntakeBig,
          ddIntakeSmall: form.value.ddIntakeSmall,
          ddIntakeMethod: form.value.ddIntakeMethod,
          ddIntakeTax: form.value.ddIntakeTax,
          ddDownlineIntake: form.value.ddDownlineIntake,
          ddFirstPrizeCommission: form.value.ddFirstPrizeCommission,
          ddSecondPrizeCommission: form.value.ddSecondPrizeCommission,
          ddThirdPrizeCommission: form.value.ddThirdPrizeCommission,
          ddStarterPrizeCommission: form.value.ddStarterPrizeCommission,
          ddConsolationPrizeCommission: form.value.ddConsolationPrizeCommission,
        },
      },
      refetchQueries: [
        {
          query: queries.user,
          variables: {
            userId: props.user.uplineId,
          },
        },
        {
          query: queries.user,
          variables: {
            userId: props.user.userId,
          },
        },
      ],
    });
    loading.value = false;
    addSuccessToast("4D Game setting updated.");
    // toggleModal(false);
  } catch (err) {
    loading.value = false;
    errorMutationNotification(err);
  }
}


return { __sfc: true,props, emit, form, loading, $t, canIntake, showPlaceOut, showCompanyPl, showUplinePl, showManagementExpense, currUserId, toggleModal, getDefaultForm, resetForm, ticketRebateOptions, expensesPercentOptions, companyPlOptions, placeoutPlOptions, uplineIntakePlOptions, intakeMethodOptions, prizeCommissionOptions, handleSubmit, ExpensesMode }
}

})